import React, { useState, useEffect } from "react";
import { db, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { collection, addDoc, serverTimestamp, doc, getDoc, query, where, getDocs, updateDoc, increment, runTransaction } from "firebase/firestore";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { FiShoppingCart, FiMapPin, FiUser, FiPhone, FiMail, FiCheck, FiTruck, FiTag } from "react-icons/fi";
import { useCart } from "../context/CartContext";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { sendOrderConfirmation } from "../utils/email";
import { useAuth } from "../context/AuthContext";
import { calcShipping } from "../utils/shipping";
import { bundleDeliveryOf } from "../utils/bundles";

// ── Step indicator ────────────────────────────────────────
function Steps({ step }) {
    const steps = ["Cart", "Information", "Payment"];
    return (
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 48 }}>
            {steps.map((s, i) => (
                <React.Fragment key={s}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                        <div style={{
                            width: 36, height: 36, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700,
                            background: i < step ? "var(--green-primary)" : i === step ? "var(--accent)" : "#e8e6e0",
                            color: i <= step ? "white" : "#aaa",
                            transition: "all 0.3s"
                        }}>
                            {i < step ? <FiCheck size={18} /> : i + 1}
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: i === step ? "var(--green-primary)" : "#aaa" }}>{s}</span>
                    </div>
                    {i < steps.length - 1 && (
                        <div style={{ height: 2, flex: 1, alignSelf: "center", marginBottom: 20, background: i < step ? "var(--green-primary)" : "#e8e6e0", transition: "background 0.3s" }} />
                    )}
                </React.Fragment>
            ))}
        </div>
    );
}

// A robust helper to find a product document in Firestore by ID, SKU, or Title
async function lookupProduct(idOrSku, metaMapping = {}) {
    const cleanId = idOrSku.trim();
    if (!cleanId) return { snap: null, selectedFormat: null };

    // Try looking up in the Meta Mapping first
    let mappedId = cleanId;
    let mappedFormat = null;

    // Check if the ID matches a key in the metaMapping
    // e.g. "uryqrfk9s9" -> "61Dz8VpA7AVUjaRzR5CB"
    if (metaMapping[cleanId]) {
        mappedId = metaMapping[cleanId];
    } else {
        // Also check with format suffix split
        const splitChar = cleanId.includes("_") ? "_" : cleanId.includes("-") ? "-" : null;
        if (splitChar) {
            const parts = cleanId.split(splitChar);
            const base = parts[0].trim();
            if (metaMapping[base]) {
                mappedId = metaMapping[base];
                mappedFormat = parts.slice(1).join(splitChar).trim();
            }
        }
    }

    // 1. Try direct Firestore ID lookup
    try {
        const docRef = doc(db, "products", mappedId);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
            return { snap, selectedFormat: mappedFormat };
        }
    } catch (e) {
        console.warn("Direct ID lookup failed, trying fallbacks...", e);
    }

    // 2. Try splitting by underscore or hyphen (e.g. ID_Format or Title_Format)
    const splitChar = mappedId.includes("_") ? "_" : mappedId.includes("-") ? "-" : null;
    let possibleId = mappedId;
    let possibleFormat = mappedFormat;
    if (splitChar && !possibleFormat) {
        const parts = mappedId.split(splitChar);
        possibleId = parts[0].trim();
        possibleFormat = parts.slice(1).join(splitChar).trim();
        
        try {
            const docRef = doc(db, "products", possibleId);
            const snap = await getDoc(docRef);
            if (snap.exists()) {
                return { snap, selectedFormat: possibleFormat };
            }
        } catch (e) {}
    }

    // 3. Try searching by metaContentId (exact match in Firestore)
    try {
        const metaQuery = query(collection(db, "products"), where("metaContentId", "==", possibleId));
        const metaSnap = await getDocs(metaQuery);
        if (!metaSnap.empty) {
            return { snap: metaSnap.docs[0], selectedFormat: possibleFormat };
        }
    } catch (e) {}

    // 4. Try searching by Title (exact match)
    try {
        const titleQuery = query(collection(db, "products"), where("title", "==", possibleId));
        const titleSnap = await getDocs(titleQuery);
        if (!titleSnap.empty) {
            return { snap: titleSnap.docs[0], selectedFormat: possibleFormat };
        }
    } catch (e) {}

    // 4. Try case-insensitive search by Title or Document ID
    try {
        const allSnap = await getDocs(collection(db, "products"));
        const match = allSnap.docs.find(d => {
            const title = d.data().title || "";
            return title.toLowerCase().trim() === possibleId.toLowerCase() || 
                   d.id.toLowerCase() === possibleId.toLowerCase();
        });
        if (match) {
            return { snap: match, selectedFormat: possibleFormat };
        }
    } catch (e) {}

    return { snap: null, selectedFormat: null };
}

export default function Checkout({ generalConfig }) {
    const { items: contextCartItems, clearCart } = useCart();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAdmin } = useAuth();

    const [metaItems, setMetaItems] = useState(null);
    const [loadingMeta, setLoadingMeta] = useState(false);

    const [step, setStep] = useState(1); // 1=info, 2=payment
    const [payConfig, setPayConfig] = useState(null);
    const [shipping, setShipping] = useState(null);
    const [shippingMethod, setShippingMethod] = useState("standard"); // standard | pickup

    const [form, setForm] = useState({
        customerName: "", email: "", phone: "",
        shippingLine1: "", shippingCity: "", shippingState: "", shippingZip: "", shippingCountry: "US"
    });
    const [errors, setErrors] = useState({});
    const [placing, setPlacing] = useState(false);
    
    // Coupons
    const [couponCode, setCouponCode] = useState("");
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [checkingCoupon, setCheckingCoupon] = useState(false);
    
    // ATH Móvil Flow
    const [athConfirming, setAthConfirming] = useState(false);
    const [createdOrderId, setCreatedOrderId] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const productsParam = params.get("products");
        const couponParam = params.get("coupon");

        if (productsParam) {
            setLoadingMeta(true);
            if (couponParam) setCouponCode(couponParam);
            
            async function fetchMetaProducts() {
                try {
                    // Try to load the Meta Catalog mapping from siteConfig/metaMapping, falling back to the default mappings
                    let currentMapping = {
                        "uryqrfk9s9": "9fFd2zmiAPLLaW0FC4WY", // Forsaken
                        "oghfsczgx2": "61Dz8VpA7AVUjaRzR5CB", // Under the Sky
                        "r9q3tmltql": "IcO81TTT40K4KrtFyNOv", // La Reina para un Rey
                        "z9kqkj1kvf": "N2AWAA6GTU7GTXRjpHwt", // Bajo La Luna
                        "g2tok8wftg": "d6trRakRIgnXCB3pH0GV", // El Sr Kingsman
                        "ochjyjd6tm": "7IHgSjcRZX5yuarXFMa6", // Kingsman
                        "kz680zfbrp": "IiTn7YPPGPSZALu8Yc55", // A Queen for a King
                        "glp33ih9y7": "rjZBv9vtjWRnDa4LMiZb", // DFurious New Stage
                        "e9f5ulmyuw": "2u65fPaC4DtKeTfLex0M", // Taiga
                        "n93d5d51z9": "UVSJXMbUEjN4WmszleQV"  // Desamparado
                    };
                    try {
                        const mapSnap = await getDoc(doc(db, "siteConfig", "metaMapping"));
                        if (mapSnap.exists()) {
                            currentMapping = { ...currentMapping, ...mapSnap.data() };
                        }
                    } catch (err) {
                        console.warn("Could not fetch metaMapping from Firestore:", err);
                    }

                    const pairs = productsParam.split(",");
                    const fetchedItems = [];
                    for (const pair of pairs) {
                        const [id, qtyStr] = pair.split(":");
                        const qty = parseInt(qtyStr, 10) || 1;
                        
                        const { snap, selectedFormat } = await lookupProduct(id, currentMapping);
                        if (snap) {
                            const data = snap.data();
                            let format = data.format || "Paperback";
                            let price = data.price || 0;
                            let isDigital = data.isDigital || false;
                            let epubStoragePath = data.epubStoragePath || "";
                            let bundleDelivery = data.bundleFormat || null;
                            let bundleItems = data.bundleItems || [];
                            
                            if (data.variants && data.variants.length > 0) {
                                let v = null;
                                if (selectedFormat) {
                                    v = data.variants.find(varObj => 
                                        varObj.format.toLowerCase() === selectedFormat.toLowerCase()
                                    );
                                }
                                if (!v) {
                                    v = data.variants.find(varObj => varObj.format === "Paperback") || data.variants[0];
                                }
                                format = v.format;
                                price = v.price;
                                isDigital = v.format === "eBook" || v.format === "Audiobook" || (v.format === "Bundle" && bundleDeliveryOf(v) === "Digital");
                                epubStoragePath = v.epubStoragePath || "";
                                bundleDelivery = bundleDeliveryOf(v) || null;
                                bundleItems = v.bundleItems || [];
                            } else {
                                isDigital = data.isDigital || format === "eBook" || (format === "Bundle" && bundleDelivery === "Digital");
                            }
                            
                            fetchedItems.push({
                                id: snap.id,
                                productId: snap.id,
                                title: data.title,
                                price: Number(price),
                                quantity: qty,
                                imageUrl: data.imageUrl,
                                selectedFormat: format,
                                isDigital: isDigital,
                                cartId: `${snap.id}_${format}`,
                                epubStoragePath: epubStoragePath,
                                bundleDelivery: bundleDelivery,
                                bundleItems: bundleItems,
                                metaContentId: data.metaContentId || null
                            });
                        } else {
                            // Fallback mock product for Meta validation tools
                            fetchedItems.push({
                                id: id,
                                productId: id,
                                title: `Book (ID: ${id})`,
                                price: 15.00,
                                quantity: qty,
                                imageUrl: generalConfig?.siteLogo || "",
                                selectedFormat: "Paperback",
                                isDigital: false,
                                cartId: `${id}_Paperback`,
                                epubStoragePath: ""
                            });
                            console.warn(`Meta Checkout: Product not found with ID/Title "${id}". Loaded fallback mock product.`);
                        }
                    }
                    if (fetchedItems.length > 0) {
                        setMetaItems(fetchedItems);
                    }
                } catch (e) {
                    console.error("Meta products error:", e);
                }
                setLoadingMeta(false);
            }
            fetchMetaProducts();
        }
    }, [location.search]);

    const cartItems = metaItems || contextCartItems;

    useEffect(() => {
        async function load() {
            try {
                const [paySnap, shipSnap] = await Promise.all([
                    getDoc(doc(db, "siteConfig", "payments")),
                    getDoc(doc(db, "siteConfig", "shipping")),
                ]);
                if (paySnap.exists()) setPayConfig(paySnap.data());
                if (shipSnap.exists()) setShipping(shipSnap.data());
            } catch (e) { console.error(e); }
        }
        load();
    }, []);

    const totalQty = cartItems.reduce((s, i) => s + i.quantity, 0);
    const subtotal = cartItems.reduce((s, i) => s + i.price * i.quantity, 0);

    // Calculate Discount
    let discountAmount = 0;
    if (appliedCoupon) {
        if (appliedCoupon.applicableItems.length === 0) {
            // Site-wide
            discountAmount = appliedCoupon.type === "percentage" 
                ? (subtotal * (appliedCoupon.value / 100)) 
                : Math.min(subtotal, appliedCoupon.value);
        } else {
            // Specific items
            const eligibleSubtotal = cartItems
                .filter(i => appliedCoupon.applicableItems.includes(i.productId) || appliedCoupon.applicableItems.includes(i.id))
                .reduce((s, i) => s + i.price * i.quantity, 0);
            
            discountAmount = appliedCoupon.type === "percentage"
                ? (eligibleSubtotal * (appliedCoupon.value / 100))
                : Math.min(eligibleSubtotal, appliedCoupon.value);
        }
    }

    const hasPhysical = cartItems.some(i => !i.isDigital);
    const hasDigital = cartItems.some(i => i.isDigital);

    const { cost: shippingCost, label: shippingLabel } = !hasPhysical
        ? { cost: 0, label: "Digital Delivery" }
        : shippingMethod === "pickup"
            ? { cost: 0, label: shipping?.localPickupLabel || "Local Pickup" }
            : calcShipping(shipping, cartItems, subtotal);
            
    const total = Math.max(0, subtotal - discountAmount + shippingCost);

    function handleChange(e) {
        const { name, value } = e.target;
        setForm(f => ({ ...f, [name]: value }));
        setErrors(er => ({ ...er, [name]: "" }));
    }

    function validate() {
        const req = ["customerName", "email", "phone"];
        if (hasPhysical && shippingMethod === "standard") {
            req.push("shippingLine1", "shippingCity", "shippingState", "shippingZip");
        }
        const newErrors = {};
        req.forEach(k => { if (!form[k] || !form[k].trim()) newErrors[k] = "Required"; });
        if (form.email && !/\S+@\S+\.\S+/.test(form.email)) newErrors.email = "Invalid email";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    async function handleApplyCoupon() {
        if (!couponCode.trim()) return;
        setCheckingCoupon(true);
        try {
            const q = query(collection(db, "coupons"), where("code", "==", couponCode.toUpperCase().trim()));
            const snap = await getDocs(q);
            
            if (snap.empty) { toast.error("Invalid coupon code."); setCheckingCoupon(false); return; }
            
            const data = { id: snap.docs[0].id, ...snap.docs[0].data() };
            const now = new Date();

            if (!data.isActive) { toast.error("This coupon is no longer active."); }
            else if (data.usageLimit && data.usedCount >= data.usageLimit) { toast.error("Coupon usage limit reached."); }
            else if (data.startDate && new Date(data.startDate) > now) { toast.error("Coupon not yet active."); }
            else if (data.endDate && (() => {
                const end = new Date(data.endDate);
                end.setHours(23, 59, 59, 999);
                return end < now;
            })()) { toast.error("Coupon has expired."); }
            else if (subtotal < data.minOrderAmount) { toast.error(`Minimum order of $${data.minOrderAmount} required.`); }
            else {
                setAppliedCoupon(data);
                toast.success(`Coupon applied: ${data.code}`);
            }
        } catch (e) {
            console.error(e);
            toast.error("Error applying coupon.");
        }
        setCheckingCoupon(false);
    }

    async function createOrder(paymentMethod, paymentId = null, status = "paid") {
        // Combo bundles ship physical copies but also include digital downloads
        const hasDigital = cartItems.some(i => i.isDigital || i.bundleDelivery === "Combo");
        const orderItems = cartItems.map(i => ({
            id: i.id, title: i.title, price: i.price,
            category: i.category || "General",
            quantity: i.quantity, imageUrl: i.imageUrl || null,
            isDigital: i.isDigital || false,
            epubStoragePath: i.epubStoragePath || null,
            bundleItems: i.bundleItems || null,
            bundleDelivery: i.bundleDelivery || null,
            format: i.selectedFormat || "Paperback",
            metaContentId: i.metaContentId || null
        }));

        const counterRef = doc(db, "counters", "orders");
        
        try {
            const orderId = await runTransaction(db, async (transaction) => {
                const counterSnap = await transaction.get(counterRef);
                let nextId = 1001;
                if (counterSnap.exists()) {
                    nextId = counterSnap.data().lastId + 1;
                }
                
                const newOrderId = String(nextId);
                const orderRef = doc(db, "orders", newOrderId);

                const orderData = {
                    ...form,
                    items: orderItems,
                    subtotal,
                    discountAmount,
                    appliedCoupon: appliedCoupon ? { id: appliedCoupon.id, code: appliedCoupon.code } : null,
                    shippingCost,
                    shippingMethod: shippingMethod === "pickup" ? (shipping?.localPickupLabel || "Local Pickup") : shippingLabel,
                    total,
                    paymentMethod,
                    paymentId,
                    status,
                    createdAt: serverTimestamp(),
                    clientUserAgent: navigator.userAgent || "",
                    // Digital delivery window: 12 hours from now (ms timestamp)
                    ...(hasDigital && { downloadExpiresAt: Date.now() + 12 * 60 * 60 * 1000 }),
                };
                
                transaction.set(orderRef, orderData);
                transaction.set(counterRef, { lastId: nextId }, { merge: true });
                
                return newOrderId;
            });

            // Trigger Email Confirmation
            if (payConfig?.emailEnabled && status === "paid") {
                sendOrderConfirmation({ id: orderId, ...form, items: orderItems, total }, payConfig, generalConfig);
            }

            // Increment coupon usage
            if (appliedCoupon) {
                await updateDoc(doc(db, "coupons", appliedCoupon.id), {
                    usedCount: increment(1)
                });
            }

            return orderId;
        } catch (e) {
            console.error("Order creation failed:", e);
            throw e;
        }
    }

    async function handleAthPay() {
        if (!validate()) return;
        setPlacing(true);
        try {
            // Create the order as pending first to get an ID
            const orderId = await createOrder("ath_movil", null, "pending_ath_verification");
            setCreatedOrderId(orderId);
            setStep(3); // Show instructions
        } catch (e) {
            console.error("ATH Pay Error:", e);
            toast.error(`Error: ${e.message || "Failed to initiate ATH Móvil payment."}`);
        }
        setPlacing(false);
    }

    async function handleAthConfirmed() {
        clearCart();
        toast.success("Order received! We will verify your payment soon.");
        navigate(`/order/${createdOrderId}`);
    }


    async function handleStripePay() {
        if (!validate()) return;
        setPlacing(true);
        try {
            // 1. Create a pending order first
            const orderId = await createOrder("stripe", null, "pending_payment");

            // 2. Create Stripe Session
            const createSession = httpsCallable(functions, "createStripeCheckout");
            const result = await createSession({
                orderId: orderId, // Pass the ID
                items: cartItems,
                shippingMethod,
                couponCode: appliedCoupon?.code || null,
                customerInfo: form,
                origin: window.location.origin
            });
            
            const { url } = result.data;
            if (url) {
                window.location.href = url;
            } else {
                throw new Error("No session URL returned");
            }
        } catch (e) {
            console.error(e);
            toast.error(e.message || "Stripe integration failed.");
        }
        setPlacing(false);
    }

    // ── TEST ORDER: creates a real Firestore order, no payment processor needed ──
    async function handleTestOrder() {
        setPlacing(true);
        try {
            const orderId = await createOrder("test", `TEST-${Date.now()}`);
            clearCart();
            toast.success("Test order created! Redirecting…");
            navigate(`/order/${orderId}`);
        } catch (e) {
            console.error(e);
            toast.error("Failed to create test order. Check Firestore rules.");
        }
        setPlacing(false);
    }

    async function handlePayPalApprove(data, actions) {
        setPlacing(true);
        try {
            const details = await actions.order.capture();
            const orderId = await createOrder("paypal", details.id);
            clearCart();
            navigate(`/order/${orderId}`);
        } catch (e) {
            toast.error("Payment capture failed. Please try again.");
        }
        setPlacing(false);
    }

    if (loadingMeta) {
        return (
            <div className="container" style={{ padding: "100px 20px", textAlign: "center" }}>
                <div className="spinner" style={{ margin: "0 auto 20px", border: "4px solid #f3f3f3", borderTop: "4px solid var(--green-primary)", borderRadius: "50%", width: 40, height: 40, animation: "spin 1s linear infinite" }} />
                <h2>Loading checkout...</h2>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    if (cartItems.length === 0 && step !== 2) {
        return (
            <div className="container" style={{ padding: "100px 20px", textAlign: "center" }}>
                <FiShoppingCart size={60} style={{ color: "#ccc", marginBottom: 20 }} />
                <h2>Your cart is empty</h2>
                <a href="/shop" style={{ color: "var(--accent)", fontWeight: 700 }}>Browse Books →</a>
            </div>
        );
    }

    return (
        <div className="checkout-page container" style={{ padding: "60px 20px 100px" }}>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "2.5rem", textAlign: "center", marginBottom: 48 }}>Checkout</h1>

            <Steps step={step} />

            <div style={{ display: "flex", gap: 60, flexWrap: "wrap", alignItems: "flex-start" }}>

                {/* ── LEFT: Form / Payment ── */}
                <div style={{ flex: "2 1 500px" }}>

                    {step === 1 && (
                        <div className="checkout-card">
                            <h3 style={{ marginBottom: 28, fontSize: "1.2rem", display: "flex", alignItems: "center", gap: 10, color: "var(--green-primary)" }}>
                                <FiUser size={20} /> Contact & Shipping Information
                            </h3>

                            <div className="checkout-form-grid">
                                <div className="cf-field full">
                                    <label>Full Name <span className="req">*</span></label>
                                    <input name="customerName" value={form.customerName} onChange={handleChange} placeholder="Jane Doe" className={errors.customerName ? "error" : ""} />
                                    {errors.customerName && <span className="field-error">{errors.customerName}</span>}
                                </div>
                                <div className="cf-field">
                                    <label><FiMail size={13} /> Email <span className="req">*</span></label>
                                    <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="jane@example.com" className={errors.email ? "error" : ""} />
                                    {errors.email && <span className="field-error">{errors.email}</span>}
                                </div>
                                <div className="cf-field">
                                    <label><FiPhone size={13} /> Phone <span className="req">*</span></label>
                                    <input name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="+1 787 000-0000" className={errors.phone ? "error" : ""} />
                                    {errors.phone && <span className="field-error">{errors.phone}</span>}
                                </div>
                            </div>

                            {/* Delivery Method */}
                            {hasPhysical && (
                                <div style={{ margin: "28px 0 20px" }}>
                                    <label style={{ fontWeight: 700, fontSize: 14, display: "block", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                                        <FiTruck size={15} /> Delivery Method
                                    </label>
                                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                                        <label className={`delivery-opt ${shippingMethod === "standard" ? "active" : ""}`}>
                                            <input type="radio" name="shippingMethod" value="standard" checked={shippingMethod === "standard"} onChange={e => setShippingMethod(e.target.value)} style={{ display: "none" }} />
                                            <FiTruck size={18} />
                                            <div>
                                                <p style={{ fontWeight: 700, margin: 0 }}>Ship to Address</p>
                                                <p style={{ margin: 0, fontSize: 12, color: "#888" }}>
                                                    {shippingMethod === "standard"
                                                        ? shippingCost === 0 ? "Free" : `$${shippingCost.toFixed(2)}`
                                                        : calcShipping(shipping, totalQty, subtotal).cost === 0 ? "Free" : `$${calcShipping(shipping, totalQty, subtotal).cost.toFixed(2)}`
                                                    }
                                                </p>
                                            </div>
                                        </label>
                                        {shipping?.localPickupEnabled && (
                                            <label className={`delivery-opt ${shippingMethod === "pickup" ? "active" : ""}`}>
                                                <input type="radio" name="shippingMethod" value="pickup" checked={shippingMethod === "pickup"} onChange={e => setShippingMethod(e.target.value)} style={{ display: "none" }} />
                                                <FiMapPin size={18} />
                                                <div>
                                                    <p style={{ fontWeight: 700, margin: 0 }}>{shipping.localPickupLabel}</p>
                                                    <p style={{ margin: 0, fontSize: 12, color: "#888" }}>Free</p>
                                                </div>
                                            </label>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Address fields */}
                            {hasPhysical && shippingMethod === "standard" && (
                                <div className="checkout-form-grid" style={{ marginTop: 8 }}>
                                    <div className="cf-field full">
                                        <label><FiMapPin size={13} /> Street Address <span className="req">*</span></label>
                                        <input name="shippingLine1" value={form.shippingLine1} onChange={handleChange} placeholder="123 Main St, Apt 4B" className={errors.shippingLine1 ? "error" : ""} />
                                        {errors.shippingLine1 && <span className="field-error">{errors.shippingLine1}</span>}
                                    </div>
                                    <div className="cf-field">
                                        <label>City <span className="req">*</span></label>
                                        <input name="shippingCity" value={form.shippingCity} onChange={handleChange} placeholder="San Juan" className={errors.shippingCity ? "error" : ""} />
                                        {errors.shippingCity && <span className="field-error">{errors.shippingCity}</span>}
                                    </div>
                                    <div className="cf-field">
                                        <label>State / Province <span className="req">*</span></label>
                                        <input name="shippingState" value={form.shippingState} onChange={handleChange} placeholder="PR" className={errors.shippingState ? "error" : ""} />
                                        {errors.shippingState && <span className="field-error">{errors.shippingState}</span>}
                                    </div>
                                    <div className="cf-field">
                                        <label>ZIP / Postal Code <span className="req">*</span></label>
                                        <input name="shippingZip" value={form.shippingZip} onChange={handleChange} placeholder="00901" className={errors.shippingZip ? "error" : ""} />
                                        {errors.shippingZip && <span className="field-error">{errors.shippingZip}</span>}
                                    </div>
                                    <div className="cf-field">
                                        <label>Country</label>
                                        <select name="shippingCountry" value={form.shippingCountry} onChange={handleChange}>
                                            <option value="US">United States</option>
                                            <option value="PR">Puerto Rico</option>
                                            <option value="CA">Canada</option>
                                            <option value="MX">Mexico</option>
                                            <option value="GB">United Kingdom</option>
                                            <option value="ES">Spain</option>
                                            <option value="OTHER">Other</option>
                                        </select>
                                    </div>
                                </div>
                            )}

                            {!hasPhysical && (
                                <div style={{ background: "#f0faf5", padding: 16, borderRadius: 10, border: "1px solid #d4edda", marginTop: 20 }}>
                                    <p style={{ margin: 0, fontSize: 14, color: "#155724", fontWeight: 600 }}>
                                        ✨ Since your order only contains digital products, no shipping address is required. 
                                        Your download links will be sent to your email after payment.
                                    </p>
                                </div>
                            )}

                            <button
                                className="checkout-primary-btn"
                                style={{ marginTop: 32, width: "100%" }}
                                onClick={() => { if (validate()) setStep(2); }}
                            >
                                Continue to Payment →
                            </button>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="checkout-card">
                            <h3 style={{ marginBottom: 28, fontSize: "1.2rem", color: "var(--green-primary)" }}>Select Payment Method</h3>

                            {!payConfig?.stripeEnabled && !payConfig?.paypalEnabled && (
                                <div style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 10, padding: 20, color: "#856404" }}>
                                    ⚠️ No payment method configured. Please ask the store admin to set up payments.
                                </div>
                            )}

                            {payConfig?.stripeEnabled && (
                                <div className="payment-option-box">
                                    <p style={{ fontWeight: 700, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                                        <span style={{ background: "#635bff", borderRadius: 6, padding: "2px 10px", color: "white", fontSize: 13 }}>Stripe</span>
                                        Credit / Debit Card
                                    </p>
                                    <p style={{ color: "#888", fontSize: 13, marginBottom: 20 }}>Secure payment via Stripe. Accepts all major credit cards.</p>
                                    <button 
                                        className="checkout-primary-btn" 
                                        style={{ background: "#635bff", width: "100%" }}
                                        onClick={handleStripePay}
                                        disabled={placing}
                                    >
                                        {placing ? "Processing..." : "Pay with Card"}
                                    </button>
                                </div>
                            )}

                            {payConfig?.paypalEnabled && payConfig?.paypalClientId && (
                                <div className="payment-option-box">
                                    <p style={{ fontWeight: 700, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                                        <span style={{ background: "#003087", borderRadius: 6, padding: "2px 10px", color: "white", fontSize: 13, fontStyle: "italic" }}>PayPal</span>
                                        Pay with PayPal
                                    </p>
                                    <PayPalScriptProvider options={{
                                        "client-id": payConfig.paypalClientId,
                                        currency: payConfig.currency || "USD",
                                    }}>
                                        <PayPalButtons
                                            style={{ layout: "vertical", color: "blue", shape: "rect", label: "pay" }}
                                            disabled={placing}
                                            createOrder={(data, actions) => actions.order.create({
                                                purchase_units: [{
                                                    amount: { value: total.toFixed(2), currency_code: payConfig.currency || "USD" },
                                                    description: `LB Books Order — ${totalQty} book(s)`
                                                }]
                                            })}
                                            onApprove={handlePayPalApprove}
                                            onError={err => { console.error(err); toast.error("PayPal error. Please try again."); }}
                                        />
                                    </PayPalScriptProvider>
                                </div>
                            )}

                            {payConfig?.athEnabled && (
                                <div className="payment-option-box" style={{ borderColor: "#ff6600", background: "#fffaf7" }}>
                                    <p style={{ fontWeight: 700, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                                        <span style={{ background: "#ff6600", borderRadius: 6, padding: "2px 10px", color: "white", fontSize: 13 }}>ATH Móvil</span>
                                        Manual Transfer
                                    </p>
                                    <p style={{ color: "#666", fontSize: 13, marginBottom: 20 }}>Pay manually using Transfer to a Person. You will get instructions with the Order ID after clicking below.</p>
                                    <button 
                                        className="checkout-primary-btn" 
                                        style={{ background: "#ff6600", width: "100%" }}
                                        onClick={handleAthPay}
                                        disabled={placing}
                                    >
                                        {placing ? "Processing..." : "Proceed to Instructions"}
                                    </button>
                                </div>
                            )}

                            {/* ── TEST ORDER BUTTON (visible only to admins) ── */}
                            {isAdmin && (
                                <div style={{ marginTop: 8, padding: 20, border: "2px dashed #f0ad4e", borderRadius: 12, background: "#fffdf0" }}>
                                    <p style={{ fontWeight: 700, fontSize: 13, color: "#856404", marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                                        🧪 Developer Test Mode
                                    </p>
                                    <p style={{ fontSize: 12, color: "#888", marginBottom: 14 }}>
                                        Skip PayPal and instantly create a real order in Firebase to test the tracking page and admin dashboard. No payment is charged.
                                    </p>
                                    <button
                                        className="test-order-btn"
                                        onClick={handleTestOrder}
                                        disabled={placing}
                                    >
                                        {placing ? "Creating order…" : "🧪 Place Test Order (No Payment)"}
                                    </button>
                                </div>
                            )}

                            <button
                                onClick={() => setStep(1)}
                                style={{ background: "none", border: "none", color: "#888", cursor: "pointer", marginTop: 20, fontSize: 14, textDecoration: "underline" }}
                            >
                                ← Back to Information
                            </button>
                        </div>
                    )}
                    {step === 3 && (
                        <div className="checkout-card" style={{ borderTop: "4px solid #ff6600" }}>
                            <h2 style={{ textAlign: "center", fontFamily: "'Playfair Display', serif", marginBottom: 32 }}>Pay with ATH Móvil</h2>
                            
                            <div style={{ background: "#f8f9fa", padding: 24, borderRadius: 12, textAlign: "center", marginBottom: 32 }}>
                                <p style={{ color: "#666", fontSize: 14, margin: "0 0 8px" }}>Send exactly:</p>
                                <p style={{ fontSize: "2.5rem", fontWeight: 800, color: "#333", margin: 0 }}>${total.toFixed(2)}</p>
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", gap: 20, color: "#444", fontSize: 15, lineHeight: 1.6 }}>
                                <p>1. Open your <strong>ATH Móvil</strong> app and select <strong>Transfer to a Person</strong>.</p>
                                <div style={{ background: "#fff5eb", padding: 16, borderRadius: 10, border: "1px solid #ffe8cc" }}>
                                    <p style={{ margin: 0, fontSize: 13, color: "#854d0e", fontWeight: 700, marginBottom: 4 }}>2. Phone Number:</p>
                                    <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#ff6600" }}>{payConfig.athPhoneNumber || "(787) XXX-XXXX"}</p>
                                </div>
                                <div style={{ background: "#eee", padding: 16, borderRadius: 10 }}>
                                    <p style={{ margin: 0, fontSize: 13, color: "#666", fontWeight: 700, marginBottom: 4 }}>3. ⚠️ IMPORTANT Message:</p>
                                    <p style={{ margin: 0, fontSize: 18, fontWeight: 700, fontFamily: "monospace" }}>{createdOrderId}</p>
                                    <p style={{ margin: "8px 0 0", fontSize: 12, color: "#888" }}>You MUST include this ID in the ATH message box.</p>
                                </div>
                            </div>

                            <button 
                                className="checkout-primary-btn" 
                                style={{ background: "#ff6600", width: "100%", marginTop: 40 }}
                                onClick={handleAthConfirmed}
                            >
                                I have sent the payment
                            </button>
                            
                            <p style={{ textAlign: "center", fontSize: 13, color: "#888", marginTop: 20 }}>
                                After clicking, we will verify the transfer and process your order.
                            </p>
                        </div>
                    )}
                </div>

                {/* ── RIGHT: Order Summary ── */}
                <div style={{ flex: "1 1 320px" }}>
                    <div className="checkout-card">
                        <h3 style={{ marginBottom: 20, fontSize: "1.1rem", color: "var(--green-primary)" }}>Order Summary</h3>

                        {cartItems.map(item => (
                            <div key={item.id} style={{ display: "flex", gap: 14, marginBottom: 16, alignItems: "center" }}>
                                {item.imageUrl && (
                                    <div style={{ position: "relative" }}>
                                        <img src={item.imageUrl} alt={item.title} style={{ width: 52, height: 68, objectFit: "cover", borderRadius: 6 }} />
                                        <span style={{ position: "absolute", top: -8, right: -8, background: "var(--green-primary)", color: "white", borderRadius: "50%", width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>
                                            {item.quantity}
                                        </span>
                                    </div>
                                )}
                                <div style={{ flex: 1 }}>
                                    <p style={{ fontWeight: 600, fontSize: 14, margin: 0 }}>{item.title}</p>
                                    <p style={{ color: "#888", fontSize: 13, margin: 0 }}>${item.price.toFixed(2)} each</p>
                                </div>
                                <strong>${(item.price * item.quantity).toFixed(2)}</strong>
                            </div>
                        ))}

                        <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 20, marginTop: 8 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "#666", marginBottom: 10 }}>
                                <span>Subtotal ({totalQty} item{totalQty !== 1 ? "s" : ""})</span>
                                <span>${subtotal.toFixed(2)}</span>
                            </div>
                            {appliedCoupon && (
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "#27ae60", marginBottom: 10 }}>
                                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                        <FiTag size={12} /> Discount ({appliedCoupon.code})
                                        <button 
                                            onClick={() => setAppliedCoupon(null)}
                                            style={{ background: "none", border: "none", color: "#e74c3c", cursor: "pointer", fontSize: 11, padding: 0, marginLeft: 4, textDecoration: "underline" }}
                                        >
                                            Remove
                                        </button>
                                    </span>
                                    <span>-${discountAmount.toFixed(2)}</span>
                                </div>
                            )}
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "#666", marginBottom: 16 }}>
                                <span>Shipping</span>
                                <span>{shippingCost === 0 ? <span style={{ color: "green", fontWeight: 700 }}>Free</span> : `$${shippingCost.toFixed(2)}`}</span>
                            </div>

                            {/* Coupon Input */}
                            <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                                <input 
                                    value={couponCode} 
                                    onChange={e => setCouponCode(e.target.value)}
                                    placeholder="Discount Code"
                                    style={{ flex: 1, padding: "10px 14px", border: "1.5px solid #eee", borderRadius: 8, fontSize: 13, outline: "none" }}
                                />
                                <button 
                                    onClick={handleApplyCoupon}
                                    disabled={checkingCoupon || !couponCode.trim()}
                                    style={{ 
                                        padding: "10px 16px", background: "#f0ede8", border: "none", borderRadius: 8, 
                                        fontSize: 13, fontWeight: 700, cursor: "pointer", transition: "all 0.2s" 
                                    }}
                                >
                                    {checkingCoupon ? "..." : "Apply"}
                                </button>
                            </div>

                            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 18, color: "var(--green-primary)" }}>
                                <span>Total</span>
                                <span>${total.toFixed(2)}</span>
                            </div>
                        </div>

                        {shippingCost === 0 && shippingMethod !== "pickup" && shipping?.freeShippingEnabled && (
                            <div style={{ marginTop: 16, background: "#d4edda", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#155724", fontWeight: 600 }}>
                                🎉 You qualify for free shipping!
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style>{`
                .checkout-card {
                    background: white;
                    border-radius: 16px;
                    padding: 32px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
                    border: 1px solid #f0ede8;
                    margin-bottom: 24px;
                }
                .checkout-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
                .cf-field { display: flex; flex-direction: column; gap: 6px; }
                .cf-field.full { grid-column: 1 / -1; }
                .cf-field label { font-size: 13px; font-weight: 700; color: #555; display: flex; align-items: center; gap: 6px; }
                .cf-field input, .cf-field select { padding: 13px 16px; border-radius: 10px; border: 1.5px solid #e8e6e0; font-size: 14px; outline: none; transition: border-color 0.2s; background: #fafaf8; }
                .cf-field input:focus, .cf-field select:focus { border-color: var(--green-primary); background: white; }
                .cf-field input.error { border-color: #e74c3c; }
                .field-error { font-size: 12px; color: #e74c3c; }
                .req { color: #e74c3c; }
                .delivery-opt { display: flex; align-items: center; gap: 12px; padding: 14px 20px; border-radius: 10px; border: 2px solid #eee; cursor: pointer; flex: 1 1 180px; transition: all 0.2s; }
                .delivery-opt.active { border-color: var(--green-primary); background: #f0faf5; }
                .checkout-primary-btn {
                    background: var(--green-primary);
                    color: white;
                    border: none;
                    padding: 16px 32px;
                    border-radius: 12px;
                    font-weight: 700;
                    font-size: 16px;
                    cursor: pointer;
                    transition: all 0.3s;
                }
                .checkout-primary-btn:hover:not(:disabled) { background: #0a3928; transform: translateY(-1px); }
                .payment-option-box { padding: 24px; border: 1.5px solid #eee; border-radius: 12px; margin-bottom: 20px; }
                .test-order-btn {
                    width: 100%;
                    background: #f0ad4e;
                    color: #5a3e00;
                    border: none;
                    padding: 14px 24px;
                    border-radius: 10px;
                    font-weight: 700;
                    font-size: 15px;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .test-order-btn:hover:not(:disabled) { background: #e09933; }
                .test-order-btn:disabled { opacity: 0.6; cursor: not-allowed; }
                @media (max-width: 600px) {
                    .checkout-form-grid { grid-template-columns: 1fr; }
                    .cf-field.full { grid-column: 1; }
                }
            `}</style>
        </div>
    );
}
